import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { alertKey, etCalendarDate, etToday } from './wiseguy/alertStore';
import {
  SESSION_PICKER_LOOKBACK_DAYS,
  isWeekend,
  lastWeekdayOnOrBefore,
} from './wiseguy/sessionDate';
import {
  EARNINGS_PHASE_CLASS,
  TOP_BET_GLYPH,
  TOP_SWEEP_GLYPH,
  WEEK52_CLASS,
  earningsBadge,
  week52Badge,
} from './wiseguy/tickerChrome';
import { useWiseguyAlerts } from '../hooks/useWiseguyAlerts';
import { useWiseguyTickerMeta } from '../hooks/useWiseguyTickerMeta';
import {
  formatConditionCombined,
  formatDelta,
  formatFlag,
  formatIv,
  formatIvChgPct,
  formatLast,
  formatOi,
  formatQty,
  formatSide,
} from './wiseguy/alertFormat';
import { alertMatchesPresetFilters } from './wiseguyAlertFilters';
import { profileService } from '../services/profileService';
import { isQfConnected, onQfConnectionChange } from '../services/qfClient';
import { useTickerLink } from '../contexts/TickerLinkContext';
import { TickerLinkButton } from './TickerLinkButton';
import { WiseguyFilterPresetModal } from './WiseguyFilterPresetModal';
import { WiseguySettingsModal } from './WiseguySettingsModal';
import { TickerTagInput } from './TickerTagInput';
import { PortalDropdown } from './PortalDropdown';
import { WiseguyDetailModal } from './WiseguyDetailModal';
import type {
  WiseguyAlert,
  WiseguyAlertFilters,
  WiseguyFlowStats,
  WiseguyFilterPreset,
  WiseguyWidgetSettings,
  WiseguyWidgetConfig,
  WiseguyColumn,
  HighlightableColumn,
  WiseguyAudioSettings,
  TTSColumn,
  Bias,
  AssetClass,
} from '../types/wiseguy';
import { addNotification } from '../utils/notificationHistory';

interface WiseguyAlertsWidgetProps {
  windowId: string;
  onRemove: () => void;
}

const INVERSE_ETFS = new Set([
  'SH', 'SDS', 'SPXU', 'DOG', 'DXD', 'SDOW', 'PSQ', 'QID', 'SQQQ', 'RWM', 'TWM', 'SRTY',
  'MYY', 'MZZ', 'SMDD', 'SBB', 'SDD', 'SKF', 'SEF', 'REW', 'SMN', 'SZK', 'SCC', 'RXD',
  'SIJ', 'SDP', 'DUG', 'SSG', 'SRS', 'REK', 'BIS', 'EFZ', 'EFU', 'EPV', 'EWV', 'YXI',
  'FXP', 'EUM', 'EEV', 'BZQ', 'SVXY', 'UVXY', 'VIXY', 'KOLD', 'SCO', 'ZSL', 'GLL',
  'SPXS', 'SPDN', 'MIDZ', 'TZA', 'FAZ', 'SOXS', 'TECS', 'LABD', 'DUST', 'JDST', 'YANG',
  'EDZ', 'ERY', 'DRIP', 'WEBS', 'AIBD', 'AAPD', 'AMDD', 'AMZD', 'AVS', 'BOED', 'BRKD',
  'TSLQ', 'NVDS', 'SARK', 'APLZ', 'BEZ', 'VXX', 'UVIX', 'BERZ', 'BNKD',
]);

// Default visible set aligns with QF board defaults (field map), minus Hits.
// Secondary columns are in order but hidden so users can toggle them on.
/**
 * Bear/bull split as one inline bar, sat on the title bar.
 *
 * Replaced a 100px-tall block of two donut charts behind a "Show Gauges"
 * toggle. Same three numbers, no vertical cost, so it no longer has to be
 * something the reader opts into — a balance you have to switch on is one
 * nobody looks at.
 *
 * ⚠️ Reads the FILTERED set, not the feed. With a preset applied it describes
 * what is on screen, which is the only thing the number can honestly claim.
 */
function FlowBalance({ bearPercent, bullPercent, ratio }: {
  bearPercent: number;
  bullPercent: number;
  /** Bear over bull. Already formatted — may be a dash. */
  ratio: string;
}) {
  return (
    <div className="hidden md:flex items-center gap-2 flex-shrink-0 ml-auto">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-bear-red">Bear</span>
      <span className="font-numeric text-[11px] text-gray-200 tabular-nums w-8 text-right">
        {bearPercent}%
      </span>

      {/* One bar, split — the two sides always total 100, so a single track
          says more than two separate meters. */}
      <div className="w-24 h-1.5 rounded-full overflow-hidden flex bg-dark-surface-light">
        <div className="bg-bear-red transition-all duration-500" style={{ width: `${bearPercent}%` }} />
        <div className="bg-bull-blue transition-all duration-500" style={{ width: `${bullPercent}%` }} />
      </div>

      <span className="font-numeric text-[11px] text-gray-200 tabular-nums w-8">{bullPercent}%</span>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-bull-blue">Bull</span>

      <div className="w-px h-4 bg-dark-border mx-1" />
      <span className="font-numeric text-[11px] font-semibold text-accent-yellow" title="Bear / bull ratio">
        {ratio}
      </span>
    </div>
  );
}

/**
 * Column order for a new user, and the order the settings list offers.
 *
 * The first ten are the visible set (see `defaultColumnVisibility`), sequenced
 * so a row reads left to right the way the alert is spoken: when, what, which
 * side, where it was, the contract, then the qualifiers.
 *
 * ⚠️ **Three columns describe "what kind", and their keys do not match their
 * labels.** Read the key, not the heading:
 *
 *  • `type` → "C/P"   — Call or Put, from `contract_type`
 *  • `flag` → "Type"  — sweep / trade / split
 *  • `side` → "Side"  — the aggressor (`aggressor_class` / `side_descriptor`)
 *
 * `type` was re-added because Call/Put appeared nowhere as text: it survived
 * only as the colour of the bias dot and in that dot's tooltip.
 */
const DEFAULT_COLUMN_ORDER: WiseguyColumn[] = [
  'time', 'symbol', 'type', 'strike', 'dte', 'expiry', 'refPrice', 'size', 'qty', 'comment',
  // Available, off by default
  'condition', 'side', 'flag', 'mult', 'bias', 'assetClass',
  'volume', 'iv', 'ivChgPct', 'last', 'delta', 'oi',
];

/**
 * A switch, for the audio and notification rows.
 *
 * ⚠️ Replaces a bare coloured dot. A dot states a condition but does not offer
 * an action — it reads as a status light, so the row looked like a readout
 * rather than something to click. The track-and-knob shape is what says "this
 * is a control, and it has two positions".
 *
 * `pending` is the third state these rows genuinely have: switched on, but not
 * yet usable — audio that the browser has not let us prime, or notifications
 * the OS has not granted. It shows gold rather than the blue of a working
 * toggle, so "on but blocked" cannot be mistaken for "on".
 *
 * Presentational only: the row around it owns the click.
 */
function Toggle({ on, pending = false }: { on: boolean; pending?: boolean }): React.ReactElement {
  return (
    <span
      aria-hidden
      /* ⚠️ Kept narrow. The dot this replaced was 12px; every pixel beyond that
         comes out of the label beside it, and Browser Notifications is already
         the longest row in a 256px menu. */
      className={`relative inline-flex flex-none items-center w-6 h-3.5 rounded-full transition-colors ${
        on ? (pending ? 'bg-accent-yellow/70' : 'bg-bull-blue') : 'bg-gray-600'
      }`}
    >
      <span
        className={`absolute w-2.5 h-2.5 rounded-full bg-white shadow transition-transform ${
          on ? 'translate-x-3' : 'translate-x-0.5'
        }`}
      />
    </span>
  );
}

/**
 * The scope caption, with any ISO date rewritten as `MM/DD/YYYY`.
 *
 * ⚠️ **Formatted here, not in `useWiseguyAlerts`.** The hook returns the raw
 * session date because other callers key off it; rewriting it there would turn
 * a value into a display string for everyone.
 *
 * ⚠️ In search mode `scopeLabel` is not a date at all — it reads
 * "AAPL · last 365d" — so anything that does not match a plain `YYYY-MM-DD` is
 * passed through untouched rather than fed to a date parser.
 *
 * Split and reordered rather than `new Date(...)`: parsing a bare date string
 * gives UTC midnight, which renders as the day before anywhere west of
 * Greenwich. The parts are already correct; they only need rearranging.
 */
function fmtScopeLabel(label: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(label);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : label;
}

const COLUMN_LABELS: Record<WiseguyColumn, string> = {
  time: 'Time',
  symbol: 'Symbol',
  type: 'C/P',
  expiry: 'Expiry',
  dte: 'DTE',
  strike: 'Strike',
  size: 'Prem',
  mult: 'Mult',
  bias: 'Bias',
  flag: 'Type',
  side: 'Side',
  qty: 'Qty',
  volume: 'Vol',
  iv: 'IV',
  ivChgPct: 'IVΔ%',
  last: 'Last',
  delta: 'Δ',
  oi: 'OI',
  assetClass: 'Asset',
  refPrice: 'Spot',
  condition: 'Condition',
  comment: 'Comment',
};

/**
 * All columns off except the eight in the default visible set.
 *
 * ⚠️ Replaces the QF-aligned set from #98. Bias, Side and Type(Sweep) are OFF —
 * direction is already on every row as the coloured dot beside the symbol, and
 * Mult tells you little at a glance. What is left is the trade itself: when,
 * what, where price was, the contract, and any note attached to it.
 */
const defaultColumnVisibility = (): Record<WiseguyColumn, boolean> => {
  const vis = {} as Record<WiseguyColumn, boolean>;
  for (const col of DEFAULT_COLUMN_ORDER) {
    vis[col] = false;
  }
  for (const col of [
    'time', 'symbol', 'type', 'strike', 'dte', 'expiry', 'refPrice', 'size', 'qty', 'comment',
  ] as WiseguyColumn[]) {
    vis[col] = true;
  }
  return vis;
};

const defaultSettings: WiseguyWidgetSettings = {
  columnVisibility: defaultColumnVisibility(),
  columnOrder: DEFAULT_COLUMN_ORDER,
  highlightRules: {},
};

const KNOWN_COLUMNS = new Set<string>(DEFAULT_COLUMN_ORDER);

/** Fixed row pitch for the virtualized tape (py-1.5 + text-xs + border). */
const ALERT_ROW_HEIGHT = 33;
const ALERT_HEADER_HEIGHT = 37;

const defaultFilters: WiseguyAlertFilters = {
  symbol: '',
  bias: [],
  contract_type: '',
  asset_class: [],
  top_bet: '',
  bet_min: '',
  bet_max: '',
  multiplier_min: '',
  multiplier_max: '',
  expiry_min: '',
  expiry_max: '',
  received_on_min: '',
  received_on_max: '',
  sweeps_only: false,
  opening_only: false,
  dte_min: '',
  dte_max: '',
  baskets: [],
};

// Normalize legacy presets that used string bias/asset_class/alert_type
function normalizePresetFilters(filters: unknown): WiseguyAlertFilters {
  const raw =
    filters && typeof filters === 'object'
      ? (filters as Record<string, unknown>)
      : {};
  const normalized: WiseguyAlertFilters & { alert_type?: string } = {
    ...defaultFilters,
    ...(raw as Partial<WiseguyAlertFilters>),
  };

  // Convert legacy string bias to single-element array
  if (typeof raw.bias === 'string') {
    normalized.bias = raw.bias ? [raw.bias as Bias] : [];
  }

  // Convert legacy string asset_class to single-element array
  if (typeof raw.asset_class === 'string') {
    normalized.asset_class = raw.asset_class ? [raw.asset_class as AssetClass] : [];
  }

  // Convert legacy alert_type string to sweeps_only boolean
  if (typeof raw.alert_type === 'string') {
    const alertType = raw.alert_type.toLowerCase();
    if (alertType === 'sweep' || alertType === 'multisweep') {
      normalized.sweeps_only = true;
    }
  }
  delete normalized.alert_type;

  // Ensure new fields have defaults
  if (normalized.sweeps_only === undefined) normalized.sweeps_only = false;
  if (normalized.opening_only === undefined) normalized.opening_only = false;
  if (normalized.dte_min === undefined) normalized.dte_min = '';
  if (normalized.dte_max === undefined) normalized.dte_max = '';
  if (!Array.isArray(normalized.baskets)) normalized.baskets = [];
  // Retired client product gate — strip so saved WSJ presets cannot re-clamp.
  delete normalized.autopost_mode;

  return normalized;
}

// Deprecated preset IDs (product gates / admin queue) — drop from saved state
const DEPRECATED_PRESET_IDS = new Set([
  'opening-sweeps',
  'admin-queue',
  'wsj-default', // old system WSJ clamp; server floor replaces it
]);

function cleanPresets(userPresets: WiseguyFilterPreset[]): WiseguyFilterPreset[] {
  return userPresets
    .filter((p) => !DEPRECATED_PRESET_IDS.has(p.id))
    .map((p) => ({
      ...p,
      isDefault: false,
      filters: normalizePresetFilters(p.filters),
    }));
}

// Built-in system widget config — the default column layout for new widgets
const DEFAULT_VIEW_CONFIG: WiseguyWidgetConfig = {
  id: 'default-view',
  name: 'Default View',
  isDefault: true,
  createdAt: '2025-01-01T00:00:00Z',
  filterPresets: [],
  activePresetId: null,
  showGauges: false,
  settings: {
    columnVisibility: defaultColumnVisibility(),
    columnOrder: DEFAULT_COLUMN_ORDER,
    /**
     * ⚠️ Colours only were changed here — every threshold and `enabled` flag is
     * untouched. Each value must exist in `DEFAULT_COLORS` in
     * `WiseguySettingsModal`, or the picker shows the rule as unset while it is
     * still colouring rows.
     */
    highlightRules: {
      // Light gold, replacing bull-blue: a highlight on the size column sat
      // next to the bias dot, where blue already means bullish.
      size: { enabled: true, minValue: 250000, color: '#dcc07a' },
      // ⚠️ OFF by default. `>= 1` means volume merely matched open interest,
      // which tints most of the feed — the row that is always lit tells you
      // nothing. The threshold and colour are kept so switching it on is
      // sensible; the colour is slate because stock blue (#3b82f6) is no
      // longer a swatch, and a default outside DEFAULT_COLORS renders with
      // nothing selected.
      mult: { enabled: false, minValue: 1, color: '#5b7d97' },
      // Bone, replacing stock #8b5cf6.
      dte: { enabled: true, maxValue: 5, color: '#f4f2ed' },
    },
  },
  audioSettings: {
    soundEnabled: false,
    ttsEnabled: false,
    ttsColumns: ['symbol', 'bias', 'type', 'strike', 'expiry', 'dte', 'size', 'mult', 'alertType'],
    ttsRate: 1.0,
    // ⚠️ OFF, like the other two. Nothing in this panel should start making
    // noise or raising OS-level popups for a user who never asked it to.
    notificationsEnabled: false,
  },
};

const SYSTEM_CONFIGS: WiseguyWidgetConfig[] = [DEFAULT_VIEW_CONFIG];
const SYSTEM_CONFIG_IDS = new Set(SYSTEM_CONFIGS.map((c) => c.id));

// Ensure system configs are always present with latest definitions
function ensureSystemConfigs(userConfigs: WiseguyWidgetConfig[]): WiseguyWidgetConfig[] {
  const filtered = userConfigs.filter((c) => !SYSTEM_CONFIG_IDS.has(c.id));
  return [...SYSTEM_CONFIGS, ...filtered];
}

const defaultAudioSettings: WiseguyAudioSettings = {
  soundEnabled: false,
  ttsEnabled: false,
  ttsColumns: ['symbol', 'bias', 'type', 'strike', 'size'],
  ttsRate: 1.0,
  notificationsEnabled: false,
};

const TTS_COLUMN_LABELS: Record<TTSColumn, string> = {
  symbol: 'Symbol',
  bias: 'Bias',
  type: 'Call/Put',
  strike: 'Strike',
  expiry: 'Expiry',
  dte: 'DTE',
  size: 'Size',
  mult: 'Multiplier',
  alertType: 'Alert Type',
};

// Play a beep sound using Web Audio API
// Frequency varies by bias: Bullish=880Hz (A5), Bearish=440Hz (A4), Neutral/default=660Hz (E5)
const playBeep = async (audioContext: AudioContext | null, bias?: Bias) => {
  if (!audioContext) return;

  // Resume AudioContext if it's suspended (browser autoplay policy)
  if (audioContext.state === 'suspended') {
    try {
      await audioContext.resume();
    } catch (e) {
      console.warn('Could not resume AudioContext:', e);
      return;
    }
  }

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // Set frequency based on bias
  const frequency = bias === 'Bullish' ? 880 : bias === 'Bearish' ? 440 : 660;
  oscillator.frequency.value = frequency;
  oscillator.type = 'sine';

  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.15);
};

// Start a silent keep-alive oscillator to prevent AudioContext from suspending when tab loses focus
// Returns a cleanup function to stop the oscillator
const startAudioKeepAlive = (audioContext: AudioContext): (() => void) => {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  // Silent: gain set to 0
  gainNode.gain.setValueAtTime(0, audioContext.currentTime);
  oscillator.frequency.value = 1; // 1Hz - inaudible
  oscillator.type = 'sine';

  oscillator.start();

  return () => {
    try {
      oscillator.stop();
      oscillator.disconnect();
      gainNode.disconnect();
    } catch {
      // Already stopped
    }
  };
};

// Speak alert using TTS
const speakAlert = (alert: WiseguyAlert, columns: TTSColumn[], rate: number) => {
  if (!('speechSynthesis' in window)) return;

  const parts: string[] = [];

  columns.forEach((col) => {
    switch (col) {
      case 'symbol':
        // Spell out the symbol letter by letter for clarity
        parts.push(alert.symbol.split('').join(' '));
        break;
      case 'bias':
        parts.push(alert.bias);
        break;
      case 'type':
        parts.push(alert.contract_type);
        break;
      case 'strike':
        parts.push(`${parseFloat(alert.strike)} strike`);
        break;
      case 'expiry': {
        const expDate = new Date(alert.expiry);
        parts.push(expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }));
        break;
      }
      case 'dte':
        if (alert.time_to_expiry_days !== undefined) {
          parts.push(`${alert.time_to_expiry_days} days`);
        }
        break;
      case 'size':
        parts.push(alert.bet_display);
        break;
      case 'mult':
        parts.push(`${parseFloat(alert.multiplier).toFixed(1)}x`);
        break;
      case 'alertType':
        parts.push(alert.alert_type);
        break;
    }
  });

  const text = parts.join(', ');
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = rate;
  utterance.pitch = 1;
  utterance.volume = 1;

  // Cancel any ongoing speech and speak new alert
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};

export function WiseguyAlertsWidget({ windowId, onRemove }: WiseguyAlertsWidgetProps) {
  // Alerts stream from QF Centrifugo, so the live indicator must track QF — not
  // portal. The two transports fail independently: portal up with QF down was
  // showing a green "Live" dot over a feed that had stopped ticking.
  const [isConnected, setIsConnected] = useState<boolean>(() => isQfConnected());
  useEffect(() => onQfConnectionChange(setIsConnected), []);

  // Filter presets state (user-defined only; no system WSJ product gate)
  const [presets, setPresets] = useState<WiseguyFilterPreset[]>([]);
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [showGauges, setShowGauges] = useState(true);
  const [isStateLoaded, setIsStateLoaded] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPreset, setEditingPreset] = useState<WiseguyFilterPreset | null>(null);

  // Settings state
  const [settings, setSettings] = useState<WiseguyWidgetSettings>(defaultSettings);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'columns' | 'highlights'>('columns');

  // Detail modal state (row click)
  const [detailAlert, setDetailAlert] = useState<WiseguyAlert | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Ticker linking
  const { isWindowLinked, setLinkedTicker } = useTickerLink();

  // Column drag state
  const [draggedColumn, setDraggedColumn] = useState<WiseguyColumn | null>(null);

  // Quick filters (in addition to preset filters)
  /**
   * The session the tape is pinned to, as an ET `YYYY-MM-DD`.
   *
   * Empty string = live: the hook falls back to `etToday()` and the board
   * follows the day-flip. Any date pins history to that session.
   *
   * ⚠️ **Deliberately NOT persisted to `frontend_state`.** Every other setting
   * in this widget is saved, and this one is the exception on purpose: a
   * dashboard that reopens on last Tuesday looks like a dead feed, and the
   * first thing a user does about a dead feed is report it. A reload always
   * returns to live.
   *
   * ⚠️ Not named `alertsMode` — that already exists on the hook and means
   * session vs. search, which is a different axis driven by the symbol filter.
   */
  const [pinnedDate, setPinnedDate] = useState('');

  /**
   * Set when a weekend pick was snapped back to the preceding Friday, holding
   * the formatted day we moved to. Empty when there is nothing to explain.
   *
   * Exists because the snap is otherwise invisible: the user picks Saturday,
   * the field shows Friday, and without a word about it that reads as the
   * control ignoring them.
   */
  const [weekendNotice, setWeekendNotice] = useState('');

  /**
   * Retire the weekend notice a few seconds after it appears.
   *
   * ⚠️ The cleanup is not optional: without it a widget closed inside the
   * window leaves a timer holding a setState on an unmounted component, and a
   * second weekend pick would stack a second timer on top of the first.
   * Keying the effect on the notice itself means each new message restarts
   * the clock rather than inheriting the previous one's remaining time.
   */
  useEffect(() => {
    if (!weekendNotice) return;
    const t = window.setTimeout(() => setWeekendNotice(''), 4000);
    return () => window.clearTimeout(t);
  }, [weekendNotice]);

  /**
   * Unpin the board and go back to the live tape.
   *
   * One handler rather than an inline `setPinnedDate('')`, because two pieces
   * of state have to move together and there are two call sites (the × on the
   * field and the link in the empty state). Inlined, one of them eventually
   * gets updated and the other does not.
   */
  const returnToLive = useCallback(() => {
    setPinnedDate('');
    setWeekendNotice('');
  }, []);

  const [quickSymbolFilter, setQuickSymbolFilter] = useState('');
  const [quickMinDte, setQuickMinDte] = useState('');
  const [quickMaxDte, setQuickMaxDte] = useState('');
  const [quickMinStrike, setQuickMinStrike] = useState('');
  const [quickMaxStrike, setQuickMaxStrike] = useState('');

  // Track new/unread alerts (alertKey values of alerts that arrived while widget wasn't focused)
  const [unreadAlertIds, setUnreadAlertIds] = useState<Set<string>>(new Set());
  const widgetRef = useRef<HTMLDivElement>(null);

  // Scroll container ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Audio settings state
  const [audioSettings, setAudioSettings] = useState<WiseguyAudioSettings>(defaultAudioSettings);
  const [isAudioPrimed, setIsAudioPrimed] = useState(false); // True after user gesture activates audio
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioKeepAliveCleanupRef = useRef<(() => void) | null>(null);

  // Responsive toolbar menu state
  const [isToolbarMenuOpen, setIsToolbarMenuOpen] = useState(false);
  const toolbarMenuRef = useRef<HTMLDivElement>(null);

  // Configurations dropdown state
  const [isConfigsDropdownOpen, setIsConfigsDropdownOpen] = useState(false);
  const configsDropdownRef = useRef<HTMLDivElement>(null);

  // Global widget configurations (shared across all widgets)
  const [widgetConfigs, setWidgetConfigs] = useState<WiseguyWidgetConfig[]>([]);
  const [activeConfigId, setActiveConfigId] = useState<string | null>(null); // Currently loaded config
  const [showSaveConfigModal, setShowSaveConfigModal] = useState(false);
  const [newConfigName, setNewConfigName] = useState('');

  // Notification permission state
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Get active filters from preset or default (no product gate)
  const presetFilters = useMemo(() => {
    if (activePresetId) {
      const preset = presets.find((p) => p.id === activePresetId);
      return preset?.filters || defaultFilters;
    }
    return defaultFilters;
  }, [activePresetId, presets]);

  // Symbol filter drives session vs ticker-search mode in useWiseguyAlerts
  const clientSymbolFilter =
    quickSymbolFilter.trim() || (presetFilters.symbol || '').trim();

  const {
    mode: alertsMode,
    alerts: storeAlerts,
    isLoading,
    error,
    truncated,
    scopeLabel,
    sessionDate,
    recentlyUpdatedKeys,
    recentlyNewKeys,
  } = useWiseguyAlerts({
    symbolFilter: clientSymbolFilter,
    instanceId: windowId,
    /**
     * Empty string → `undefined`, so the hook takes its `|| etToday()` branch
     * and the board stays live. `''` is falsy and would work by accident, but
     * the option is typed `string | undefined` and an empty date is not a day.
     *
     * The hook does the rest: `pinnedDate` is in its fetch effect's deps, so
     * changing this clears the store and refetches; its live subscription
     * stays open and drops rows off the pinned day.
     */
    date: pinnedDate || undefined,
  });

  // Floored earnings E + 52W chrome (trust server phase; refresh ~45s).
  // Dated from the tape's session, not etToday().
  const metaSymbols = useMemo(
    () => storeAlerts.map((a) => a.symbol),
    [storeAlerts]
  );
  const { bySymbol: tickerMeta } = useWiseguyTickerMeta(metaSymbols, sessionDate);

  // Quick filter values for client-side filtering
  const quickFilters = useMemo(
    () => ({
      minDte: quickMinDte ? Number(quickMinDte) : undefined,
      maxDte: quickMaxDte ? Number(quickMaxDte) : undefined,
      minStrike: quickMinStrike ? Number(quickMinStrike) : undefined,
      maxStrike: quickMaxStrike ? Number(quickMaxStrike) : undefined,
    }),
    [quickMinDte, quickMaxDte, quickMinStrike, quickMaxStrike]
  );

  // Load state from backend on mount - uses windowId for per-widget settings
  useEffect(() => {
    const loadState = async () => {
      try {
        const response = await profileService.getFrontendState();

        // Load global widget configs (shared across all widgets)
        const savedConfigs = response.frontend_state?.wiseguyWidgetConfigs;
        setWidgetConfigs(ensureSystemConfigs(savedConfigs || []));

        // Load per-widget settings using windowId
        const savedState = response.frontend_state?.wiseguyWidgets?.[windowId];
        if (savedState) {
          const cleaned = cleanPresets(savedState.filterPresets || []);
          setPresets(cleaned);
          // User presets only; null = show full server feed (no extra filters)
          const savedActiveId = savedState.activePresetId;
          const activeId =
            savedActiveId &&
            !DEPRECATED_PRESET_IDS.has(savedActiveId) &&
            cleaned.some((p) => p.id === savedActiveId)
              ? savedActiveId
              : null;
          setActivePresetId(activeId);
          setActiveConfigId(savedState.activeConfigId ?? DEFAULT_VIEW_CONFIG.id);
          setShowGauges(savedState.showGauges ?? true);
          if (savedState.settings) {
            // Drop retired columns (e.g. ivChg); append any new columns at end.
            const rawOrder = (savedState.settings.columnOrder ?? []) as unknown[];
            const savedOrder = rawOrder.filter(
              (col): col is WiseguyColumn =>
                typeof col === 'string' && KNOWN_COLUMNS.has(col)
            );
            const missingColumns = DEFAULT_COLUMN_ORDER.filter(
              (col) => !savedOrder.includes(col)
            );
            const mergedColumnOrder = [...savedOrder, ...missingColumns];

            const savedVis = savedState.settings.columnVisibility as
              | Partial<Record<string, boolean>>
              | undefined;
            // Map retired column visibility onto replacements where useful.
            const migratedVis: Partial<Record<WiseguyColumn, boolean>> = {
              ...(savedVis as Partial<Record<WiseguyColumn, boolean>>),
            };
            if (savedVis && 'ivChg' in savedVis && migratedVis.ivChgPct === undefined) {
              migratedVis.ivChgPct = savedVis.ivChg;
            }
            // Events folded into Condition — if either was on, show condition.
            if (
              savedVis &&
              (savedVis.events === true || savedVis.condition === true) &&
              migratedVis.condition === undefined
            ) {
              migratedVis.condition = true;
            }

            setSettings({
              ...defaultSettings,
              ...savedState.settings,
              columnOrder: mergedColumnOrder,
              columnVisibility: {
                ...defaultSettings.columnVisibility,
                ...migratedVis,
              },
              highlightRules: {
                ...defaultSettings.highlightRules,
                ...savedState.settings.highlightRules,
              },
            });
          }
          if (savedState.audioSettings) {
            setAudioSettings({
              ...defaultAudioSettings,
              ...savedState.audioSettings,
            });
          }
        } else {
          // No saved state at all — load Default View config
          handleLoadWidgetConfig(DEFAULT_VIEW_CONFIG);
        }
      } catch (error) {
        console.error('Failed to load wiseguy widget state:', error);
      } finally {
        setIsStateLoaded(true);
      }
    };
    loadState();
  }, [windowId]);

  // Save state to backend (debounced)
  const saveStateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveState = useCallback(() => {
    if (!isStateLoaded) return;

    if (saveStateTimeoutRef.current) {
      clearTimeout(saveStateTimeoutRef.current);
    }

    saveStateTimeoutRef.current = setTimeout(async () => {
      try {
        // Backend uses deep merge - only send this widget's state
        await profileService.saveFrontendState({
          wiseguyWidgets: {
            [windowId]: {
              filterPresets: presets,
              activePresetId,
              activeConfigId,
              showGauges,
              settings,
              audioSettings,
            },
          },
        });
      } catch (error) {
        console.error('Failed to save wiseguy widget state:', error);
      }
    }, 1000);
  }, [windowId, presets, activePresetId, activeConfigId, showGauges, settings, audioSettings, isStateLoaded]);

  // Save state when it changes
  useEffect(() => {
    saveState();
  }, [presets, activePresetId, showGauges, settings, audioSettings, saveState]);

  // Store is mode-scoped (session today / ticker search). User filters only.
  const allAlerts = storeAlerts;

  const filteredAlerts = useMemo(() => {
    return allAlerts.filter((alert) => {
      // Multi-symbol client filter (single-symbol is already search-mode fetch)
      if (clientSymbolFilter) {
        const symbolsToMatch = clientSymbolFilter
          .split(',')
          .map((s) => s.trim().toUpperCase())
          .filter((s) => s.length > 0);

        if (symbolsToMatch.length > 1) {
          const alertSymbol = alert.symbol.toUpperCase();
          if (!symbolsToMatch.some((s) => alertSymbol === s)) return false;
        }
      }

      if (!alertMatchesPresetFilters(alert, presetFilters)) return false;

      const dte = alert.time_to_expiry_days;
      if (quickFilters.minDte !== undefined && (dte === undefined || dte < quickFilters.minDte)) {
        return false;
      }
      if (quickFilters.maxDte !== undefined && (dte === undefined || dte > quickFilters.maxDte)) {
        return false;
      }

      const strike = parseFloat(alert.strike);
      if (quickFilters.minStrike !== undefined && strike < quickFilters.minStrike) {
        return false;
      }
      if (quickFilters.maxStrike !== undefined && strike > quickFilters.maxStrike) {
        return false;
      }

      return true;
    });
  }, [allAlerts, presetFilters, quickFilters, clientSymbolFilter]);

  // Calculate flow statistics
  const flowStats = useMemo((): WiseguyFlowStats => {
    const stats: WiseguyFlowStats = {
      bullishCount: 0,
      bearishCount: 0,
      neutralCount: 0,
      bullishVolume: 0,
      bearishVolume: 0,
      totalAlerts: filteredAlerts.length,
    };

    filteredAlerts.forEach((alert) => {
      if (alert.bias === 'Bullish') {
        stats.bullishCount++;
        stats.bullishVolume += alert.bet;
      } else if (alert.bias === 'Bearish') {
        stats.bearishCount++;
        stats.bearishVolume += alert.bet;
      } else {
        stats.neutralCount++;
      }
    });

    return stats;
  }, [filteredAlerts]);

  // Audio / notifications for newly arrived live alerts that match filters
  const seenNewKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const key of recentlyNewKeys) {
      if (seenNewKeysRef.current.has(key)) continue;
      seenNewKeysRef.current.add(key);
      const alertData = allAlerts.find((a) => alertKey(a) === key);
      if (!alertData) continue;
      if (
        !matchesFilters(
          alertData,
          { ...presetFilters, symbol: clientSymbolFilter || presetFilters.symbol },
          quickFilters
        )
      ) {
        continue;
      }

      if (document.hidden) {
        setUnreadAlertIds((prev) => new Set(prev).add(key));
      }
      if (audioSettings.soundEnabled && isAudioPrimed && audioContextRef.current) {
        playBeep(audioContextRef.current, alertData.bias);
      }
      if (audioSettings.ttsEnabled && audioSettings.ttsColumns.length > 0) {
        speakAlert(alertData, audioSettings.ttsColumns, audioSettings.ttsRate);
      }
      if (audioSettings.notificationsEnabled && document.hidden) {
        showAlertNotification(alertData);
      }
    }
  }, [
    recentlyNewKeys,
    allAlerts,
    presetFilters,
    clientSymbolFilter,
    quickFilters,
    audioSettings,
    isAudioPrimed,
  ]);

  // Clear unread when filters change
  useEffect(() => {
    setUnreadAlertIds(new Set());
  }, [clientSymbolFilter, presetFilters, quickFilters]);

  // Clear unread alerts when widget is clicked/interacted with
  const handleWidgetInteraction = useCallback(() => {
    if (unreadAlertIds.size > 0) {
      setUnreadAlertIds(new Set());
    }
  }, [unreadAlertIds.size]);

  // Cleanup audio keep-alive on unmount
  useEffect(() => {
    return () => {
      if (audioKeepAliveCleanupRef.current) {
        audioKeepAliveCleanupRef.current();
        audioKeepAliveCleanupRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // Handle row click — open read-only detail modal for all users
  const handleAlertRowClick = useCallback((alert: WiseguyAlert) => {
    setDetailAlert(alert);
    setIsDetailModalOpen(true);
  }, []);

  // Handle symbol click - filters this widget and syncs to linked charts
  const handleSymbolClick = useCallback(
    (e: React.MouseEvent, symbol: string) => {
      e.stopPropagation(); // Don't trigger row click (detail modal)
      const upperSymbol = symbol.toUpperCase();
      // Update the quick filter
      setQuickSymbolFilter(upperSymbol);
      // If this widget is linked, broadcast to other linked charts
      if (isWindowLinked(windowId)) {
        setLinkedTicker(upperSymbol);
      }
    },
    [windowId, isWindowLinked, setLinkedTicker]
  );

  // Handle ticker change from TickerLinkButton (when linked ticker changes externally)
  const handleLinkedTickerChange = useCallback(
    (ticker: string) => {
      setQuickSymbolFilter(ticker);
    },
    []
  );

  // Outside click handling for dropdowns is managed by PortalDropdown component

  // Toggle a TTS column
  const handleToggleTTSColumn = (column: TTSColumn) => {
    setAudioSettings((prev) => {
      const newColumns = prev.ttsColumns.includes(column)
        ? prev.ttsColumns.filter((c) => c !== column)
        : [...prev.ttsColumns, column];
      return { ...prev, ttsColumns: newColumns };
    });
  };

  // Prime audio system (must be called from user gesture)
  const primeAudio = async () => {
    if (!audioContextRef.current) {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      audioContextRef.current = new AudioCtx();
    }
    // Resume if suspended (required by browser autoplay policy)
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    // Start silent keep-alive oscillator to prevent AudioContext from suspending when tab loses focus
    if (!audioKeepAliveCleanupRef.current) {
      audioKeepAliveCleanupRef.current = startAudioKeepAlive(audioContextRef.current);
    }

    setIsAudioPrimed(true);
    // Play a test beep to confirm it works
    playBeep(audioContextRef.current);
  };

  // Toggle sound enabled (with audio context init)
  const handleToggleSound = async () => {
    // When enabling, prime the audio system
    if (!audioSettings.soundEnabled) {
      await primeAudio();
    }
    setAudioSettings((prev) => ({ ...prev, soundEnabled: !prev.soundEnabled }));
  };

  // Toggle TTS enabled
  const handleToggleTTS = async () => {
    // When enabling TTS, prime the audio system (for future sound alerts)
    if (!audioSettings.ttsEnabled && !isAudioPrimed) {
      await primeAudio();
    }
    setAudioSettings((prev) => ({ ...prev, ttsEnabled: !prev.ttsEnabled }));
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    if (Notification.permission === 'granted') {
      setNotificationPermission('granted');
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission === 'granted';
    }

    return false;
  };

  // Show browser notification for an alert
  const showAlertNotification = (alert: WiseguyAlert) => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;

    const biasEmoji = alert.bias === 'Bullish' ? '🟢' : alert.bias === 'Bearish' ? '🔴' : '⚪';
    const title = `${biasEmoji} ${alert.symbol} ${alert.contract_type}`;
    const body = `$${parseFloat(alert.strike).toFixed(0)} ${new Date(alert.expiry).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })} | ${alert.bet_display} | ${parseFloat(alert.multiplier).toFixed(1)}x`;

    // Store in notification history
    addNotification({ title, body, source: 'wiseguy' });

    const notification = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: `wiseguy-${alert.id}`, // Prevents duplicate notifications
      requireInteraction: false,
    });

    // Focus the window when notification is clicked
    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Auto-close after 5 seconds
    setTimeout(() => notification.close(), 5000);
  };

  // Toggle notifications enabled
  const handleToggleNotifications = async () => {
    if (!audioSettings.notificationsEnabled) {
      // Enabling - request permission first
      const granted = await requestNotificationPermission();
      if (granted) {
        setAudioSettings((prev) => ({ ...prev, notificationsEnabled: true }));
        // Show test notification
        if (typeof Notification !== 'undefined') {
          new Notification('Wiseguy Alerts', {
            body: 'Notifications enabled! You\'ll receive alerts when the tab is in the background.',
            icon: '/favicon.ico',
          });
        }
      }
    } else {
      // Disabling
      setAudioSettings((prev) => ({ ...prev, notificationsEnabled: false }));
    }
  };

  // Check if alert matches filters (including quick filters for DTE/Strike)
  const matchesFilters = (
    alert: WiseguyAlert,
    currentFilters: WiseguyAlertFilters,
    clientFilters?: { minDte?: number; maxDte?: number; minStrike?: number; maxStrike?: number }
  ): boolean => {
    // Handle comma-separated symbols for quick filter
    if (currentFilters.symbol) {
      const symbolsToMatch = currentFilters.symbol
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s.length > 0);

      if (symbolsToMatch.length > 0) {
        const alertSymbol = alert.symbol.toUpperCase();
        const matches = symbolsToMatch.some((s) => alertSymbol === s);
        if (!matches) return false;
      }
    }

    // Delegate to shared preset filter function
    if (!alertMatchesPresetFilters(alert, currentFilters)) return false;

    // Client-side quick filters for DTE and Strike
    if (clientFilters) {
      const dte = alert.time_to_expiry_days;
      if (clientFilters.minDte !== undefined && (dte === undefined || dte < clientFilters.minDte)) {
        return false;
      }
      if (clientFilters.maxDte !== undefined && (dte === undefined || dte > clientFilters.maxDte)) {
        return false;
      }

      const strike = parseFloat(alert.strike);
      if (clientFilters.minStrike !== undefined && strike < clientFilters.minStrike) {
        return false;
      }
      if (clientFilters.maxStrike !== undefined && strike > clientFilters.maxStrike) {
        return false;
      }
    }

    return true;
  };

  // Preset management handlers
  const handleSelectPreset = (presetId: string | null) => {
    // null = no preset (full server feed + quick filters only)
    setActivePresetId(presetId);
  };

  const handleEditPreset = (preset: WiseguyFilterPreset) => {
    setEditingPreset(preset);
    setIsModalOpen(true);
  };

  const handleDeletePreset = (presetId: string) => {
    setPresets((prev) => prev.filter((p) => p.id !== presetId));
    if (activePresetId === presetId) {
      setActivePresetId(null);
    }
  };

  const handleAddNew = () => {
    setEditingPreset(null);
    setIsModalOpen(true);
  };

  const handleSavePreset = (preset: WiseguyFilterPreset) => {
    setPresets((prev) => {
      const existing = prev.find((p) => p.id === preset.id);
      if (existing) {
        return prev.map((p) => (p.id === preset.id ? preset : p));
      }
      return [...prev, preset];
    });
    setActivePresetId(preset.id);
  };

  // Settings handler
  const handleSaveSettings = (newSettings: WiseguyWidgetSettings) => {
    setSettings(newSettings);
  };

  /**
   * Symbol colouring: Accent Gold + bias dot, or Call-blue / Put-red ticker.
   *
   * Written into `settings` rather than its own piece of state so it rides the
   * existing persistence — the save effect already watches `settings`, and
   * named widget configs already snapshot it — instead of needing a second
   * field threaded through both.
   */
  const handleToggleColorSymbolByType = () => {
    setSettings((prev) => ({ ...prev, colorSymbolByType: !prev.colorSymbolByType }));
  };

  // Save current widget config as a named preset
  const handleSaveWidgetConfig = async (name: string) => {
    const newConfig: WiseguyWidgetConfig = {
      id: `config-${Date.now()}`,
      name,
      createdAt: new Date().toISOString(),
      filterPresets: presets,
      activePresetId,
      showGauges,
      settings,
      audioSettings,
      quickFilters: {
        symbol: quickSymbolFilter,
        minDte: quickMinDte,
        maxDte: quickMaxDte,
        minStrike: quickMinStrike,
        maxStrike: quickMaxStrike,
      },
    };

    const updatedConfigs = [...widgetConfigs, newConfig];
    setWidgetConfigs(updatedConfigs);

    // Save to backend
    try {
      await profileService.saveFrontendState({
        wiseguyWidgetConfigs: updatedConfigs,
      });
    } catch (error) {
      console.error('Failed to save widget config:', error);
    }

    setShowSaveConfigModal(false);
    setNewConfigName('');
  };

  // Load a widget config into this widget
  const handleLoadWidgetConfig = (config: WiseguyWidgetConfig) => {
    setPresets(cleanPresets(config.filterPresets || []));
    const cfgActive = config.activePresetId;
    setActivePresetId(
      cfgActive && !DEPRECATED_PRESET_IDS.has(cfgActive) ? cfgActive : null
    );
    setShowGauges(config.showGauges);
    setSettings(config.settings);
    setAudioSettings(config.audioSettings);
    // Restore quick filters if present
    if (config.quickFilters) {
      setQuickSymbolFilter(config.quickFilters.symbol || '');
      setQuickMinDte(config.quickFilters.minDte || '');
      setQuickMaxDte(config.quickFilters.maxDte || '');
      setQuickMinStrike(config.quickFilters.minStrike || '');
      setQuickMaxStrike(config.quickFilters.maxStrike || '');
    }
    setActiveConfigId(config.id); // Track which config is loaded
    setIsToolbarMenuOpen(false);
  };

  // Update the currently active config with current settings
  const handleUpdateWidgetConfig = async () => {
    if (!activeConfigId) return;
    // Cannot update system configs
    if (SYSTEM_CONFIG_IDS.has(activeConfigId)) return;

    const existingConfig = widgetConfigs.find((c) => c.id === activeConfigId);
    if (!existingConfig) return;

    const updatedConfig: WiseguyWidgetConfig = {
      ...existingConfig,
      filterPresets: presets,
      activePresetId,
      showGauges,
      settings,
      audioSettings,
      quickFilters: {
        symbol: quickSymbolFilter,
        minDte: quickMinDte,
        maxDte: quickMaxDte,
        minStrike: quickMinStrike,
        maxStrike: quickMaxStrike,
      },
    };

    const updatedConfigs = widgetConfigs.map((c) =>
      c.id === activeConfigId ? updatedConfig : c
    );
    setWidgetConfigs(updatedConfigs);

    // Save to backend
    try {
      await profileService.saveFrontendState({
        wiseguyWidgetConfigs: updatedConfigs,
      });
    } catch (error) {
      console.error('Failed to update widget config:', error);
    }

    setIsConfigsDropdownOpen(false);
  };

  // Delete a widget config
  const handleDeleteWidgetConfig = async (configId: string) => {
    // Cannot delete system configs
    if (SYSTEM_CONFIG_IDS.has(configId)) return;
    const updatedConfigs = widgetConfigs.filter((c) => c.id !== configId);
    setWidgetConfigs(updatedConfigs);

    // Fall back to Default View if we're deleting the active config
    if (activeConfigId === configId) {
      handleLoadWidgetConfig(DEFAULT_VIEW_CONFIG);
    }

    // Save to backend
    try {
      await profileService.saveFrontendState({
        wiseguyWidgetConfigs: updatedConfigs,
      });
    } catch (error) {
      console.error('Failed to delete widget config:', error);
    }
  };

  // Column drag handlers for reordering
  const handleColumnDragStart = (column: WiseguyColumn) => {
    setDraggedColumn(column);
  };

  const handleColumnDragOver = (e: React.DragEvent, targetColumn: WiseguyColumn) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === targetColumn) return;

    const newOrder = [...settings.columnOrder];
    const draggedIdx = newOrder.indexOf(draggedColumn);
    const targetIdx = newOrder.indexOf(targetColumn);

    newOrder.splice(draggedIdx, 1);
    newOrder.splice(targetIdx, 0, draggedColumn);

    setSettings((prev) => ({
      ...prev,
      columnOrder: newOrder,
    }));
  };

  const handleColumnDragEnd = () => {
    setDraggedColumn(null);
  };

  // Get visible columns in order
  const visibleColumns = useMemo(() => {
    return settings.columnOrder.filter((col) => settings.columnVisibility[col] !== false);
  }, [settings.columnOrder, settings.columnVisibility]);

  const alertRowVirtualizer = useVirtualizer({
    count: filteredAlerts.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ALERT_ROW_HEIGHT,
    overscan: 16,
    paddingStart: ALERT_HEADER_HEIGHT,
    getItemKey: (index) => alertKey(filteredAlerts[index]),
  });
  const virtualAlertRows = alertRowVirtualizer.getVirtualItems();
  const alertListSize = alertRowVirtualizer.getTotalSize();
  const alertPadTop =
    virtualAlertRows.length > 0 ? virtualAlertRows[0].start - ALERT_HEADER_HEIGHT : 0;
  const alertPadBottom =
    virtualAlertRows.length > 0
      ? alertListSize - virtualAlertRows[virtualAlertRows.length - 1].end
      : 0;

  // Get numeric value for a highlightable column
  const getColumnValue = (alert: WiseguyAlert, column: HighlightableColumn): number | null => {
    switch (column) {
      case 'size': return alert.bet;
      case 'mult': return parseFloat(alert.multiplier);
      case 'dte': return alert.time_to_expiry_days ?? null;
      case 'refPrice': return parseFloat(alert.reference_price);
      default: return null;
    }
  };

  // Get highlight style for a specific cell based on column rules (range-based)
  const getCellHighlightStyle = (alert: WiseguyAlert, column: WiseguyColumn): React.CSSProperties => {
    // Only certain columns can be highlighted
    const highlightableColumns: HighlightableColumn[] = ['size', 'mult', 'dte', 'refPrice'];
    if (!highlightableColumns.includes(column as HighlightableColumn)) {
      return {};
    }

    const rule = settings.highlightRules[column as HighlightableColumn];
    if (!rule?.enabled) {
      return {};
    }

    const value = getColumnValue(alert, column as HighlightableColumn);
    if (value === null) {
      return {};
    }

    // Range-based highlighting with single color
    const hasMin = rule.minValue !== undefined;
    const hasMax = rule.maxValue !== undefined;

    // Both min and max: highlight when min <= value <= max
    if (hasMin && hasMax) {
      if (value >= rule.minValue! && value <= rule.maxValue!) {
        return { backgroundColor: `${rule.color}30`, color: rule.color };
      }
    }
    // Only min: highlight when value >= min
    else if (hasMin) {
      if (value >= rule.minValue!) {
        return { backgroundColor: `${rule.color}30`, color: rule.color };
      }
    }
    // Only max: highlight when value <= max
    else if (hasMax) {
      if (value <= rule.maxValue!) {
        return { backgroundColor: `${rule.color}30`, color: rule.color };
      }
    }

    return {};
  };

  // Format helpers
  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    // Show HH:mm for today, or MM/DD HH:mm for older dates
    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) + ' ' +
           date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  /**
   * Expiry as `09/30/2026`.
   *
   * ⚠️ All-numeric, matching `fmtUsDate` and every other date in the app. A
   * month name puts a variable-width word in a column that is otherwise
   * digits, so the dates did not line up down the table.
   *
   * ⚠️ UTC, not local. Expiries arrive date-stamped at midnight, and read in a
   * timezone west of Greenwich that renders as the day before.
   */
  const formatExpiry = (dateString: string): string => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  // Export filtered alerts to CSV
  const handleExportCSV = useCallback(() => {
    if (filteredAlerts.length === 0) return;

    const csvColumns: { key: WiseguyColumn; label: string }[] = visibleColumns.map((col) => ({
      key: col,
      label: COLUMN_LABELS[col],
    }));

    const getCellValue = (alert: WiseguyAlert, column: WiseguyColumn): string => {
      switch (column) {
        case 'time': return new Date(alert.received_on).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, year: 'numeric', month: '2-digit', day: '2-digit' });
        case 'symbol': return alert.symbol;
        // Full word: a single letter is ambiguous in a spreadsheet with no
        // colour to read it by.
        case 'type': return alert.contract_type;
        case 'bias': return alert.bias;
        case 'strike': return alert.strike;
        case 'expiry': return alert.expiry;
        case 'dte': return alert.time_to_expiry_days?.toString() ?? '';
        case 'size': return alert.bet_display || alert.bet.toString();
        case 'mult': return alert.multiplier;
        case 'flag': return alert.flag ?? '';
        case 'side': return formatSide(alert) === '-' ? '' : formatSide(alert);
        case 'qty': return alert.qty != null ? String(alert.qty) : '';
        case 'volume': return alert.volume != null ? String(alert.volume) : '';
        case 'iv': return alert.iv != null ? formatIv(alert.iv) : '';
        case 'ivChgPct': return alert.iv_chg_pct != null ? formatIvChgPct(alert.iv_chg_pct) : '';
        case 'last': return alert.last_price ?? '';
        case 'delta': return alert.delta != null ? formatDelta(alert.delta) : '';
        case 'oi': return alert.open_interest != null ? String(alert.open_interest) : '';
        case 'assetClass': return alert.asset_class;
        case 'refPrice': return alert.reference_price;
        case 'condition': {
          const combined = formatConditionCombined(alert);
          return combined === '-' ? '' : combined;
        }
        case 'comment': return alert.comment || '';
        default: return '';
      }
    };

    const escapeCsvField = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    const header = csvColumns.map((c) => escapeCsvField(c.label)).join(',');
    const rows = filteredAlerts.map((alert) =>
      csvColumns.map((c) => escapeCsvField(getCellValue(alert, c.key))).join(',')
    );
    const csv = [header, ...rows].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `wiseguy-alerts-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [filteredAlerts, visibleColumns]);

  // Calculate gauge percentages
  const bullPercent =
    flowStats.totalAlerts > 0
      ? Math.round(
          (flowStats.bullishCount /
            (flowStats.bullishCount + flowStats.bearishCount || 1)) *
            100
        )
      : 50;
  const bearPercent = 100 - bullPercent;
  const ratio =
    flowStats.bullishCount > 0
      ? (flowStats.bearishCount / flowStats.bullishCount).toFixed(2)
      : '0.00';

  return (
    <div
      ref={widgetRef}
      onClick={handleWidgetInteraction}
      className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden relative"
    >
      {/*
        FZN monogram watermark, matching GEX-Ray, Gamma Exposure and Net
        Sweeper Flow.

        ⚠️ Sits on the WIDGET, not inside the scroll container — anchored here
        it stays put while the tape scrolls past it. Inside, it would slide
        away with the rows and reappear only at the top of the list.

        ⚠️ `pointer-events-none` is load-bearing: the rows underneath are
        clickable, and an image spanning the panel would otherwise swallow
        every click that landed on it.

        ⚠️ Fainter than the charts' 0.10. Those float it over open plot area;
        here it sits behind a dense table of small text, and at 0.10 the mark
        reads through the figures rather than behind them.
      */}
      <img
        src="/logo-watermark.png"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-40 opacity-[0.05] z-0"
      />

      {/* Title Bar - drag handle only */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Wiseguy Alerts</h3>
        {isConnected && (
          <span className="w-2 h-2 rounded-full bg-bull-blue animate-pulse ml-2" title="Live" />
        )}
        {/* ⚠️ The counts and session date that sat here are gone. The footer
            already carries both — "Showing X of Y loaded · <session>" — so the
            title bar was repeating them, and it is the one row where the flow
            balance needs the width. */}

        {/* Flow balance, hard right. Display-only, so it can live on the drag
            handle without swallowing a drag — there is nothing here to click. */}
        <FlowBalance bearPercent={bearPercent} bullPercent={bullPercent} ratio={ratio} />
      </div>

      {/* Toolbar - NOT draggable, allows wrapping */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 bg-dark-bg/50 border-b border-dark-border relative z-20">
        {/* Configurations dropdown */}
        <div className="relative" ref={configsDropdownRef}>
          {(() => {
            const activeConfig = activeConfigId ? widgetConfigs.find((c) => c.id === activeConfigId) : null;
            return (
              <>
                <button
                  onClick={() => setIsConfigsDropdownOpen(!isConfigsDropdownOpen)}
                  className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
                    isConfigsDropdownOpen
                      ? 'bg-dark-surface-light text-white'
                      : activeConfig
                        ? 'text-accent-yellow hover:text-white hover:bg-dark-surface'
                        : 'text-gray-300 hover:text-white hover:bg-dark-surface'
                  }`}
                >
                  {activeConfig?.isDefault ? (
                    <svg className="w-3.5 h-3.5 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  )}
                  <span className="max-w-[100px] truncate">
                    {activeConfig ? activeConfig.name : 'Configs'}
                  </span>
                  <svg className={`w-3 h-3 transition-transform ${isConfigsDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <PortalDropdown
                  anchorRef={configsDropdownRef}
                  isOpen={isConfigsDropdownOpen}
                  onClose={() => setIsConfigsDropdownOpen(false)}
                  width={224}
                >
                    {/* Update current config (if one is active and not a system config) */}
                    {activeConfig && !activeConfig.isDefault && (
                      <button
                        onClick={handleUpdateWidgetConfig}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-bull-blue hover:text-white hover:bg-dark-surface-light transition-colors border-b border-dark-border"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Update "{activeConfig.name}"
                      </button>
                    )}
                    {/* Save as new config */}
                    <button
                      onClick={() => {
                        setShowSaveConfigModal(true);
                        setIsConfigsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-300 hover:text-white hover:bg-dark-surface-light transition-colors border-b border-dark-border"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Save As New...
                    </button>
                    {/* Reset to Default View (if not already on it) */}
                    {/* ⚠️ Always shown, including when Default View is already
                        the active config. It used to be hidden by
                        `activeConfigId !== DEFAULT_VIEW_CONFIG.id`, which meant
                        the reset disappeared exactly when you were sitting on
                        the view you wanted to reset — and since saved state is
                        restored from `frontend_state` rather than re-read from
                        the constant, being "on" Default View does not mean
                        holding its defaults. There was no way back to them. */}
                    <button
                      onClick={() => {
                        handleLoadWidgetConfig(DEFAULT_VIEW_CONFIG);
                        setIsConfigsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-400 hover:text-white hover:bg-dark-surface-light transition-colors border-b border-dark-border"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Reset to Default View
                    </button>
                    {/* Saved configs list */}
                    {widgetConfigs.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto py-1">
                        {widgetConfigs.map((config) => (
                          <div
                            key={config.id}
                            className={`flex items-center justify-between px-3 py-1.5 hover:bg-dark-surface-light group ${
                              activeConfigId === config.id ? 'bg-accent-yellow/10' : ''
                            }`}
                          >
                            <button
                              onClick={() => {
                                handleLoadWidgetConfig(config);
                                setIsConfigsDropdownOpen(false);
                              }}
                              className={`flex-1 text-left text-xs truncate flex items-center gap-1.5 ${
                                activeConfigId === config.id ? 'text-accent-yellow' : 'text-gray-300 hover:text-white'
                              }`}
                              title={`Load "${config.name}"`}
                            >
                              {activeConfigId === config.id && (
                                <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {config.isDefault && (
                                <svg className="w-3 h-3 flex-shrink-0 text-accent-yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              )}
                              {config.name}
                            </button>
                            {!config.isDefault && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteWidgetConfig(config.id);
                              }}
                              className="text-gray-500 hover:text-bear-red opacity-0 group-hover:opacity-100 transition-opacity ml-2 p-0.5"
                              title="Delete config"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-3 text-xs text-gray-500 text-center">
                        No saved configurations yet
                      </div>
                    )}
                </PortalDropdown>
              </>
            );
          })()}
        </div>
        {/* Vertical divider */}
        <div className="w-px h-5 bg-dark-border flex-shrink-0" />
        {/* Quick symbol filter */}
        <TickerTagInput
          value={quickSymbolFilter}
          onChange={setQuickSymbolFilter}
          placeholder="AAPL,NVDA"
          compact
        />
        {/* Ticker link button */}
        <TickerLinkButton
          windowId={windowId}
          currentTicker={quickSymbolFilter.split(',')[0] || ''}
          onTickerChange={handleLinkedTickerChange}
        />
        {/* DTE quick filters */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-gray-500">DTE</span>
          <input
            type="number"
            value={quickMinDte}
            onChange={(e) => setQuickMinDte(e.target.value)}
            placeholder="0"
            className="w-10 px-1 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow text-center"
            min="0"
          />
          <span className="text-[10px] text-gray-500">-</span>
          <input
            type="number"
            value={quickMaxDte}
            onChange={(e) => setQuickMaxDte(e.target.value)}
            placeholder="∞"
            className="w-10 px-1 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow text-center"
            min="0"
          />
        </div>
        {/* Strike quick filters */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-gray-500">Strike</span>
          <input
            type="number"
            value={quickMinStrike}
            onChange={(e) => setQuickMinStrike(e.target.value)}
            placeholder="0"
            className="w-14 px-1 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow text-center"
            min="0"
            step="0.5"
          />
          <span className="text-[10px] text-gray-500">-</span>
          <input
            type="number"
            value={quickMaxStrike}
            onChange={(e) => setQuickMaxStrike(e.target.value)}
            placeholder="∞"
            className="w-14 px-1 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow text-center"
            min="0"
            step="0.5"
          />
        </div>
        {/* Clear all quick filters button */}
        {(quickSymbolFilter || quickMinDte || quickMaxDte || quickMinStrike || quickMaxStrike) && (
          <button
            onClick={() => {
              setQuickSymbolFilter('');
              setQuickMinDte('');
              setQuickMaxDte('');
              setQuickMinStrike('');
              setQuickMaxStrike('');
            }}
            className="text-xs px-1.5 py-1 rounded transition-colors text-gray-400 hover:text-white hover:bg-dark-surface flex-shrink-0"
            title="Clear all quick filters"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {/*
          Session picker. Replaces the inert Live/Past pair — this one is wired.

          One field rather than a mode switch plus a date: the two would encode
          the same fact twice and could disagree. Empty means live, so the
          control has exactly one source of truth, and the × is how you get
          back.

          ⚠️ The hook needed NO change for this. `useWiseguyAlerts` already
          accepts `date`, already refetches when it changes, and already drops
          live rows that fall off a pinned day. This is the caller it was
          waiting for.

          ⚠️ **Non-trading days are NOT greyed out, and cannot be here.** A
          native date input draws its own calendar; `min`/`max` are the only
          controls it exposes, so no individual day can be disabled. Weekends
          are caught in `onChange` and snapped to the previous Friday instead.

          Market holidays are not caught at all — nothing in the app, in QF
          (`/v1/calendar/` serves earnings and economic only), or in
          `fznService` publishes a trading calendar, and a hardcoded holiday
          list goes stale each year and then fails silently. A holiday returns
          an empty tape naming the day, which is honest: there were no alerts.

          Proper greying needs both a trading-calendar source AND a custom
          calendar popover to replace this input. Worth doing together, not
          separately.
        */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-[10px] uppercase tracking-[1px] text-gray-500">Session</span>
          <div className="relative flex items-center">
            <input
              type="date"
              value={pinnedDate}
              /* Bounded both ways: no future session exists, and 90 days is
                 the history window we have actually exercised. */
              min={etCalendarDate(SESSION_PICKER_LOOKBACK_DAYS - 1)}
              max={etToday()}
              onChange={(e) => {
                const next = e.target.value;
                /*
                 * ⚠️ **The weekend guard lives HERE, not in the calendar.**
                 *
                 * A native `<input type="date">` draws its own calendar and
                 * offers no way to disable individual days — `min`/`max` bound
                 * the range and that is the whole API. So a Saturday is
                 * selectable, and this is the only place it can be refused.
                 *
                 * Rejecting it silently would look broken, so the day is
                 * snapped back to the preceding Friday and the reason is
                 * surfaced beside the field. Greying the weekends out properly
                 * needs a custom calendar popover — see the note on the
                 * control.
                 */
                if (next && isWeekend(next)) {
                  const snapped = lastWeekdayOnOrBefore(next);
                  setWeekendNotice(fmtScopeLabel(snapped));
                  setPinnedDate(snapped);
                  return;
                }
                setWeekendNotice('');
                setPinnedDate(next);
              }}
              title={
                pinnedDate
                  ? `Showing ${fmtScopeLabel(pinnedDate)} — clear to return to the live tape`
                  : 'Live tape. Pick a past session to pin the board to that day.'
              }
              aria-label="Session date"
              className={`font-numeric text-xs px-1.5 py-1 pr-5 bg-dark-surface border rounded focus:outline-none focus:border-accent-yellow [color-scheme:dark] ${
                pinnedDate
                  ? 'border-accent-yellow text-accent-yellow'
                  : 'border-dark-border text-gray-400'
              }`}
            />
            {/* Only rendered when pinned — with no date set there is nothing
                to clear, and an always-on × invites a click that does
                nothing. Absolutely positioned so showing it cannot reflow the
                toolbar. */}
            {pinnedDate && (
              <button
                onClick={returnToLive}
                title="Back to the live tape"
                aria-label="Clear session date and return to live"
                className="absolute right-0.5 text-gray-400 hover:text-white px-0.5 leading-none"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {/* The live dot is on the title bar, but it reads as "connected",
              not "showing today". This says which session is on screen. */}
          {pinnedDate && !weekendNotice && (
            <span
              className="text-[10px] uppercase tracking-[1px] text-accent-yellow font-semibold"
              title="Historical session — the live tape is not shown"
            >
              Past
            </span>
          )}
          {/* Replaces the Past badge rather than sitting beside it: the
              toolbar wraps, and two labels on one control is noise. Clears
              itself after a few seconds. */}
          {weekendNotice && (
            <span
              className="text-[10px] text-gray-400 whitespace-nowrap"
              title="Markets are closed at weekends — moved to the previous session"
            >
              Weekend → {weekendNotice}
            </span>
          )}
        </div>

        {/* ⚠️ CSV export moved into the toolbar menu, as its own Export
            section at the foot. It is a rare action that acts on the data
            rather than changing how it is displayed, so it does not belong in
            the always-visible toolbar beside the filters. */}
        {/* Vertical divider */}
        <div className="w-px h-5 bg-dark-border flex-shrink-0" />
        {/* Settings hamburger menu */}
        <div className="relative" ref={toolbarMenuRef}>
          <button
            onClick={() => setIsToolbarMenuOpen(!isToolbarMenuOpen)}
            className={`text-xs px-2 py-1 rounded transition-colors flex-shrink-0 relative ${
              isToolbarMenuOpen
                ? 'bg-dark-surface-light text-white'
                : 'text-gray-400 hover:text-white hover:bg-dark-surface'
            }`}
            title="Display & Alert Settings"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {/* Indicator dot when any alert setting is active */}
            {(audioSettings.soundEnabled || audioSettings.ttsEnabled || audioSettings.notificationsEnabled) && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-bull-blue rounded-full" />
            )}
          </button>
          <PortalDropdown
            anchorRef={toolbarMenuRef}
            isOpen={isToolbarMenuOpen}
            onClose={() => setIsToolbarMenuOpen(false)}
            width={256}
            align="right"
          >
              {/* Display Settings Section */}
              <div className="p-3 border-b border-dark-border">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Display</div>
                <div className="space-y-1">
                  {/* ⚠️ "Show Gauges" removed with the gauges themselves. The
                      balance now lives on the title bar and is always on, so a
                      toggle here would switch nothing. */}
                  {/* Lives in the cog itself rather than the Columns modal:
                      it changes how an existing column looks, not which
                      columns exist, and it wants the same switch affordance as
                      the Alerts rows below. */}
                  <button
                    onClick={handleToggleColorSymbolByType}
                    className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    <span className="text-xs text-gray-300 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4 4 4 0 014-4 4 4 0 014 4 4 4 0 01-4 4zM7 3a4 4 0 014 4M17 3a4 4 0 00-4 4m4 14a4 4 0 004-4 4 4 0 00-4-4 4 4 0 00-4 4 4 4 0 004 4z" />
                      </svg>
                      Color Symbol by C/P
                    </span>
                    <Toggle on={settings.colorSymbolByType === true} />
                  </button>
                  <button
                    onClick={() => {
                      setSettingsInitialTab('columns');
                      setIsSettingsModalOpen(true);
                      setIsToolbarMenuOpen(false);
                    }}
                    className="w-full flex items-center py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    <span className="text-xs text-gray-300 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      Column Settings
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setSettingsInitialTab('highlights');
                      setIsSettingsModalOpen(true);
                      setIsToolbarMenuOpen(false);
                    }}
                    className="w-full flex items-center py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    <span className="text-xs text-gray-300 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                      </svg>
                      Highlight Rules
                    </span>
                  </button>
                </div>
              </div>

              {/* Audio & Notifications Section */}
              <div className="p-3">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Alerts</div>
                <div className="space-y-1">
                  <button
                    onClick={handleToggleSound}
                    className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    <span className="text-xs text-gray-300 flex items-center gap-2">
                      {audioSettings.soundEnabled ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                        </svg>
                      )}
                      Sound Alerts
                    </span>
                    <Toggle on={audioSettings.soundEnabled} pending={!isAudioPrimed} />
                  </button>
                  {/* TTS toggle with expandable settings */}
                  <div>
                    <button
                      onClick={handleToggleTTS}
                      className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                    >
                      <span className="text-xs text-gray-300 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        Text-to-Speech
                      </span>
                      <Toggle on={audioSettings.ttsEnabled} pending={!isAudioPrimed} />
                    </button>
                    {audioSettings.ttsEnabled && (
                      <div className="mt-2 ml-6 p-2 bg-dark-bg rounded border border-dark-border">
                        <div className="text-[10px] text-gray-500 mb-1">Columns to read:</div>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(Object.keys(TTS_COLUMN_LABELS) as TTSColumn[]).map((column) => (
                            <button
                              key={column}
                              onClick={() => handleToggleTTSColumn(column)}
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                audioSettings.ttsColumns.includes(column)
                                  ? 'bg-accent-yellow text-hot-tag-text font-semibold'
                                  : 'bg-dark-surface text-gray-400 hover:text-white'
                              }`}
                            >
                              {TTS_COLUMN_LABELS[column]}
                            </button>
                          ))}
                        </div>
                        <div className="font-numeric text-[10px] text-gray-500 mb-1">Rate: {audioSettings.ttsRate.toFixed(1)}x</div>
                        <input
                          type="range"
                          min="0.5"
                          max="2"
                          step="0.1"
                          value={audioSettings.ttsRate}
                          onChange={(e) => setAudioSettings((prev) => ({ ...prev, ttsRate: parseFloat(e.target.value) }))}
                          className="w-full h-1 bg-dark-border rounded-lg appearance-none cursor-pointer"
                        />
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleToggleNotifications}
                    className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    {/* ⚠️ `whitespace-nowrap` and `flex-none` on the icon are
                        load-bearing. This is the longest label in the menu and
                        the only one with a trailing tag, so without them the
                        toggle squeezes it and "Browser Notifications" wraps to
                        two centred lines while every other row stays on one. */}
                    <span className="text-xs text-gray-300 flex items-center gap-2 min-w-0 text-left">
                      <svg className="w-4 h-4 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <span className="whitespace-nowrap">Browser Notifications</span>
                      {notificationPermission === 'denied' && (
                        <span className="text-[10px] text-bear-red flex-none">(blocked)</span>
                      )}
                    </span>
                    <Toggle on={audioSettings.notificationsEnabled} pending={notificationPermission !== 'granted'} />
                  </button>
                </div>
              </div>

              {/* Filter Presets Section */}
              <div className="p-3 border-t border-dark-border">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Filter Presets</div>
                <div className="space-y-1">
                  {/* Create new filter preset */}
                  <button
                    onClick={() => {
                      handleAddNew();
                      setIsToolbarMenuOpen(false);
                    }}
                    className="w-full flex items-center py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                  >
                    <span className="text-xs text-gray-300 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Create Filter Preset...
                    </span>
                  </button>
                  {activePresetId && (
                    <button
                      onClick={() => {
                        handleSelectPreset(null);
                        setIsToolbarMenuOpen(false);
                      }}
                      className="w-full flex items-center py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors"
                    >
                      <span className="text-xs text-gray-300 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Clear filter preset
                      </span>
                    </button>
                  )}
                  {/* Saved presets list */}
                  {presets.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-dark-border">
                      <div className="text-[10px] text-gray-500 mb-1">Saved Presets:</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {presets.map((preset) => (
                          <div
                            key={preset.id}
                            className={`flex items-center justify-between py-1 px-2 rounded hover:bg-dark-surface-light group ${
                              activePresetId === preset.id ? 'bg-accent-yellow/10 border border-accent-yellow/30' : ''
                            }`}
                          >
                            <button
                              onClick={() => {
                                handleSelectPreset(preset.id);
                                setIsToolbarMenuOpen(false);
                              }}
                              className="text-xs text-gray-300 hover:text-white flex-1 text-left truncate flex items-center gap-1"
                              title={`Apply "${preset.name}"`}
                            >
                              {preset.isDefault && <span className="text-accent-yellow">★</span>}
                              {preset.name}
                            </button>
                            {!preset.isDefault && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPreset(preset);
                                  setIsToolbarMenuOpen(false);
                                }}
                                className="text-gray-500 hover:text-white p-0.5"
                                title="Edit preset"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeletePreset(preset.id);
                                }}
                                className="text-gray-500 hover:text-bear-red p-0.5"
                                title="Delete preset"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Export — its own section at the foot of the menu. A rare
                  action, and one that acts on the data rather than changing
                  how it is displayed, so it does not belong among the
                  toggles. */}
              <div className="p-3 border-t border-dark-border">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Export</div>
                <button
                  onClick={() => {
                    handleExportCSV();
                    setIsToolbarMenuOpen(false);
                  }}
                  disabled={filteredAlerts.length === 0}
                  className="w-full flex items-center justify-between py-1.5 px-2 rounded hover:bg-dark-surface-light transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <span className="text-xs text-gray-300 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download CSV
                  </span>
                  {/* Says what will actually be written — the FILTERED set,
                      not the whole feed. */}
                  <span className="font-numeric text-[10px] text-gray-500">
                    {filteredAlerts.length.toLocaleString()} rows
                  </span>
                </button>
              </div>
          </PortalDropdown>
        </div>
        {/* Spacer to push close button to right */}
        <div className="flex-1" />
        {/* Close button */}
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors flex-shrink-0"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Preset Badges Row */}
      {presets.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 py-1 bg-dark-bg/30 border-b border-dark-border">
          <span className="text-[10px] text-gray-500 mr-0.5">Presets:</span>
          {presets.map((preset) => {
            const isActive = activePresetId === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => handleSelectPreset(isActive ? null : preset.id)}
                className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                  isActive
                    ? 'bg-accent-yellow text-hot-tag-text border-accent-yellow font-semibold'
                    : 'bg-dark-surface text-gray-400 border-dark-border hover:border-gray-500 hover:text-gray-300'
                }`}
                title={isActive ? `Active: "${preset.name}" (click to clear)` : `Activate "${preset.name}"`}
              >
                {preset.name}
              </button>
            );
          })}
          <button
            onClick={handleAddNew}
            className="px-1.5 py-0.5 text-[10px] rounded border border-dashed border-dark-border text-gray-500 hover:border-gray-400 hover:text-gray-300 transition-colors"
            title="New preset"
          >
            +
          </button>
        </div>
      )}

      {/* Audio Activation Banner - shows when audio is enabled but not yet primed */}
      {(audioSettings.soundEnabled || audioSettings.ttsEnabled) && !isAudioPrimed && (
        <button
          onClick={primeAudio}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-accent-yellow/20 border-b border-accent-yellow/30 text-accent-yellow text-xs hover:bg-accent-yellow/30 transition-colors cursor-pointer"
        >
          <svg className="w-4 h-4 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
          </svg>
          <span>Click here to activate audio alerts (required by browser policy)</span>
        </button>
      )}

      {/* ⚠️ The Gauges section that stood here is gone — its two donut charts
          and ratio are now `FlowBalance` on the title bar, which shows the same
          three numbers at no vertical cost. `showGauges` is still read and
          persisted so saved layouts and presets keep their shape; it simply no
          longer drives anything. */}

      {/* Alerts Table */}
      <div
        ref={scrollContainerRef}
        // `relative z-10` lifts the tape above the watermark. The table itself
        // paints no background, so the mark still shows through between rows.
        className="flex-1 overflow-auto relative z-10"
      >
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading alerts...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm">
            Error loading alerts
          </div>
        )}

        {truncated && (
          <div className="px-3 py-1 text-xs text-accent-yellow bg-dark-surface border-b border-dark-border">
            {/* ⚠️ Says the pinned day rather than "today" — on a past session
                the old copy claimed the wrong date. */}
            {alertsMode === 'search'
              ? 'Showing the newest matching alerts for this search — results were truncated server-side.'
              : `Showing the newest alerts for ${
                  pinnedDate ? fmtScopeLabel(pinnedDate) : 'today'
                } — results were truncated server-side.`}
          </div>
        )}

        {!isLoading && !error && filteredAlerts.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm gap-1">
            {/*
              A pinned day that returns nothing needs to say WHICH day, or it
              reads as a broken feed. Market holidays land here: they are not
              greyed out in the picker (no trading calendar is available), so
              this message is the only thing that explains the empty tape.
            */}
            {pinnedDate ? (
              <>
                <span>No alerts for {fmtScopeLabel(pinnedDate)}</span>
                <button
                  onClick={returnToLive}
                  className="text-xs text-accent-yellow hover:underline"
                >
                  Back to live
                </button>
              </>
            ) : (
              'No alerts found'
            )}
          </div>
        )}

        {filteredAlerts.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border z-10">
              <tr>
                {visibleColumns.map((column) => (
                  <th
                    key={column}
                    draggable
                    onDragStart={() => handleColumnDragStart(column)}
                    onDragOver={(e) => handleColumnDragOver(e, column)}
                    onDragEnd={handleColumnDragEnd}
                    /* C/P holds one character, so it takes half the padding
                       the wider columns need. */
                    className={`${column === 'type' ? 'px-1' : 'px-2'} py-2 font-semibold text-gray-300 cursor-grab select-none whitespace-nowrap ${
                      draggedColumn === column ? 'opacity-50 bg-dark-border' : ''
                    } ${
                      column === 'time' || column === 'symbol' || column === 'type' || column === 'expiry' || column === 'condition' || column === 'comment' || column === 'side' || column === 'flag'
                        ? 'text-left'
                        : column === 'strike' || column === 'size' || column === 'mult' || column === 'dte' || column === 'refPrice' || column === 'qty' || column === 'volume' || column === 'iv' || column === 'ivChgPct' || column === 'last' || column === 'delta' || column === 'oi'
                          ? 'text-right'
                          : 'text-center'
                    }`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {/* Drag grip indicator */}
                      <svg className="w-3 h-3 text-gray-500" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="6" r="1.5" />
                        <circle cx="15" cy="6" r="1.5" />
                        <circle cx="9" cy="12" r="1.5" />
                        <circle cx="15" cy="12" r="1.5" />
                        <circle cx="9" cy="18" r="1.5" />
                        <circle cx="15" cy="18" r="1.5" />
                      </svg>
                      {COLUMN_LABELS[column]}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {alertPadTop > 0 && (
                <tr aria-hidden>
                  <td
                    colSpan={visibleColumns.length}
                    style={{ height: alertPadTop, padding: 0, border: 0 }}
                  />
                </tr>
              )}
              {virtualAlertRows.map((virtualRow) => {
                const alert = filteredAlerts[virtualRow.index];
                const key = alertKey(alert);
                const isNew =
                  recentlyNewKeys.has(key) || recentlyUpdatedKeys.has(key);
                const isUnread = unreadAlertIds.has(key);
                return (
                <tr
                  key={key}
                  data-index={virtualRow.index}
                  onClick={() => handleAlertRowClick(alert)}
                  className={`border-b border-dark-border hover:bg-dark-surface-light transition-colors ${
                    alert.top_bet ? 'bg-accent-yellow/5' : ''
                  } ${isNew ? 'animate-flash-highlight' : ''} ${isUnread ? 'border-l-2 border-l-accent-yellow' : ''} cursor-pointer`}
                >
                  {visibleColumns.map((column) => {
                    const cellStyle = getCellHighlightStyle(alert, column);
                    return (
                      <td
                        key={column}
                        /*
                         * Three tones, straight off the palette in `index.css`
                         * rather than one middle grey for everything:
                         *
                         *  • NUMBERS are `text-white` (Text Primary, #F4F2ED) —
                         *    the token's own note reads "Symbols, ALL numbers,
                         *    titles, body". They also take `font-numeric`
                         *    (Inter), which is what makes a column of figures
                         *    line up; Archivo's digits are not tabular.
                         *  • SECONDARY text is `text-gray-400` (Text Muted,
                         *    #C7C2B8) — the same tone `time` already uses.
                         *  • Everything was previously `text-gray-300`
                         *    (#D6D2C8), which is neither token: numbers came
                         *    out dimmer than the spec and labels brighter, so
                         *    the table read flat.
                         */
                        className={`${column === 'type' ? 'px-1' : 'px-2'} py-1.5 whitespace-nowrap ${
                          // A clock reading is a number: Inter, so the column
                          // aligns. It keeps Text Muted — the palette assigns
                          // that tone to timestamps — so this is a typeface
                          // change only, not a brightness one.
                          column === 'time' ? 'font-numeric text-gray-400' :
                          column === 'symbol' ? '' :
                          column === 'type' ? 'text-left' :
                          column === 'expiry' ? 'font-numeric text-white' :
                          column === 'condition' || column === 'comment' || column === 'side' || column === 'flag' || column === 'assetClass' ? 'text-gray-400' :
                          column === 'strike' || column === 'size' || column === 'mult' || column === 'dte' || column === 'refPrice' || column === 'qty' || column === 'volume' || column === 'iv' || column === 'ivChgPct' || column === 'last' || column === 'delta' || column === 'oi' ? 'text-right font-numeric text-white' :
                          'text-center'
                        }`}
                        style={cellStyle}
                      >
                        {column === 'time' && formatTime(alert.received_on)}
                        {column === 'symbol' && (
                          <span className="inline-flex items-center gap-1.5 w-full">
                            {/* Direction dot, in front of the symbol — hidden
                                when the ticker is carrying the colour itself
                                (`Color Symbol by C/P` in the cog).
                                ⚠️ Reads `contract_type`, NOT `bias`, so the
                                dot, the C/P badge and the coloured-symbol mode
                                are all driven by one field and cannot
                                contradict each other on the same row.
                                ⚠️ The optional `bias` column below is
                                deliberately left alone and still reads `bias`
                                first — it answers a different question, so the
                                two can differ when both are on screen.
                                ⚠️ Like the badge, this shows the INSTRUMENT's
                                direction, not the position's: a call seller is
                                bearish but has `contract_type: 'Call'` and
                                shows blue. Fixing that needs the buy/sell
                                field described on `formatSide` above, and the
                                fix is the same inversion in all three places.
                                Blank means transparent, not absent: the cell
                                is a flex row, so dropping the element would
                                pull the ticker left and leave the Symbol
                                column ragged. `flex-shrink-0` keeps a narrow
                                column from squashing the dot into an oval. */}
                            {!settings.colorSymbolByType && (
                              <span
                                className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${
                                  alert.contract_type === 'Call'
                                    ? 'bg-bull-blue'
                                    : alert.contract_type === 'Put'
                                      ? 'bg-bear-red'
                                      : 'bg-transparent'
                                }`}
                                title={alert.contract_type || undefined}
                              />
                            )}
                            <button
                              onClick={(e) => handleSymbolClick(e, alert.symbol)}
                              /* Default is Accent Gold, matching every other
                                 ticker in the app — the flow tables on Market
                                 Overview, the Explorer hero bar — and the
                                 palette's own note on this token: "logo Z,
                                 ticker, spot".
                                 ⚠️ Was `text-blue-400`: stock Tailwind, never a
                                 Fuzion token, and it collided with `bull-blue`
                                 two cells away where blue means bullish.
                                 With `colorSymbolByType` on, the ticker takes
                                 Call-blue / Put-red from the same
                                 `contract_type` field as the C/P badge, so the
                                 two can never disagree. Gold stays the
                                 fallback even then: rows do arrive with
                                 `contract_type: ''` (see the `-` guard on that
                                 badge), and an unknown side must not be
                                 painted red as though it were a put. */
                              className={`font-semibold hover:underline cursor-pointer ${
                                !settings.colorSymbolByType
                                  ? 'text-accent-yellow'
                                  : alert.contract_type === 'Call'
                                    ? 'text-bull-blue'
                                    : alert.contract_type === 'Put'
                                      ? 'text-bear-red'
                                      : 'text-accent-yellow'
                              }`}
                              title="Click to filter and sync linked charts"
                            >
                              {alert.symbol}
                            </button>
                            {(() => {
                              const meta = tickerMeta.get(alert.symbol.toUpperCase());
                              const earn = earningsBadge(meta);
                              const w52 = week52Badge(meta);
                              return (
                                <>
                                  {earn && (
                                    <span
                                      className={`text-[10px] font-bold ${EARNINGS_PHASE_CLASS[earn.phase]}`}
                                      title={earn.title}
                                    >
                                      E
                                    </span>
                                  )}
                                  {w52 && (
                                    <span
                                      className={`text-[10px] font-bold ${WEEK52_CLASS[w52.kind]}`}
                                      title={w52.title}
                                    >
                                      52W
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            {/* Purple from the highlight swatch palette
                                (#7b61e0), replacing stock Tailwind orange.
                                Written as an arbitrary value because it is a
                                picker colour rather than a theme token — keep
                                it in step with `DEFAULT_COLORS` in
                                `WiseguySettingsModal`. */}
                            {INVERSE_ETFS.has(alert.symbol) && (
                              <span className="px-1 py-0.5 rounded text-[9px] font-medium bg-[#7b61e0]/20 text-[#7b61e0]" title="Inverse ETF">
                                INV
                              </span>
                            )}
                            <span className="ml-auto inline-flex items-center gap-1">
                              {alert.comment && (
                                <svg className="w-3.5 h-3.5 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                                  <title>{alert.comment}</title>
                                  <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z"/>
                                </svg>
                              )}
                              {alert.posted_to_discord && (
                                <a
                                  href="https://discord.com/channels/1409936885010141346/1439969603404042281"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:opacity-80 transition-opacity"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <svg className="w-3.5 h-3.5 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                                    <title>Posted to Discord - Click to open channel</title>
                                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                                  </svg>
                                </a>
                              )}
                              {alert.top_bet && (
                                <span className="text-sm leading-none" title="Top Bet">
                                  {TOP_BET_GLYPH}
                                </span>
                              )}
                              {alert.top_sweep && (
                                <span className="text-sm leading-none" title="Top Sweep">
                                  {TOP_SWEEP_GLYPH}
                                </span>
                              )}
                            </span>
                          </span>
                        )}
                        {column === 'expiry' && formatExpiry(alert.expiry)}
                        {column === 'dte' && (alert.time_to_expiry_days !== undefined ? `${alert.time_to_expiry_days}d` : '-')}
                        {column === 'strike' && parseFloat(alert.strike).toFixed(2)}
                        {column === 'size' && alert.bet_display}
                        {column === 'mult' && (
                          alert.multiplier !== '' && !Number.isNaN(parseFloat(alert.multiplier))
                            ? `${parseFloat(alert.multiplier).toFixed(1)}x`
                            : <span className="text-gray-600">-</span>
                        )}
                        {column === 'type' && (
                          alert.contract_type ? (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                alert.contract_type === 'Call'
                                  ? 'bg-bull-blue/20 text-bull-blue'
                                  : 'bg-bear-red/20 text-bear-red'
                              }`}
                              title={alert.contract_type}
                            >
                              {alert.contract_type === 'Call' ? 'C' : 'P'}
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          )
                        )}
                        {column === 'bias' && (
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${
                              alert.bias === 'Bullish'
                                ? 'bg-bull-blue'
                                : alert.bias === 'Bearish'
                                  ? 'bg-bear-red'
                                  : alert.contract_type === 'Call'
                                    ? 'bg-bull-blue'
                                    : 'bg-bear-red'
                            }`}
                            title={`${alert.bias} · ${alert.contract_type}`}
                          />
                        )}
                        {column === 'flag' && (
                          alert.flag ? (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                alert.flag === 'sweep'
                                  ? 'bg-accent-yellow/20 text-accent-yellow'
                                  : alert.flag === 'split'
                                    ? 'bg-purple-500/20 text-purple-300'
                                    : 'bg-gray-700/50 text-gray-300'
                              }`}
                              title={alert.alert_type}
                            >
                              {formatFlag(alert.flag)}
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          )
                        )}
                        {column === 'side' && (
                          <span className="text-gray-300 text-[11px]">{formatSide(alert)}</span>
                        )}
                        {column === 'qty' && (
                          <span className="font-numeric">{formatQty(alert.qty)}</span>
                        )}
                        {column === 'volume' && (
                          <span className="font-numeric" title="Session option volume at alert">
                            {formatOi(alert.volume)}
                          </span>
                        )}
                        {column === 'iv' && (
                          <span className="font-numeric">{formatIv(alert.iv)}</span>
                        )}
                        {column === 'ivChgPct' && (
                          <span
                            className={`font-numeric ${
                              alert.iv_chg_pct != null && alert.iv_chg_pct > 0
                                ? 'text-bull-blue'
                                : alert.iv_chg_pct != null && alert.iv_chg_pct < 0
                                  ? 'text-bear-red'
                                  : ''
                            }`}
                          >
                            {formatIvChgPct(alert.iv_chg_pct)}
                          </span>
                        )}
                        {column === 'last' && (
                          <span className="font-numeric">{formatLast(alert.last_price)}</span>
                        )}
                        {column === 'delta' && (
                          <span className="font-numeric">{formatDelta(alert.delta)}</span>
                        )}
                        {column === 'oi' && (
                          <span className="font-numeric">{formatOi(alert.open_interest)}</span>
                        )}
                        {column === 'assetClass' && (
                          <span className="text-gray-400 text-[10px]">{alert.asset_class}</span>
                        )}
                        {column === 'refPrice' && (
                          alert.reference_price !== '' && !Number.isNaN(parseFloat(alert.reference_price))
                            ? parseFloat(alert.reference_price).toFixed(2)
                            : <span className="text-gray-600">-</span>
                        )}
                        {column === 'condition' && (() => {
                          const text = formatConditionCombined(alert);
                          return text !== '-' ? (
                            <span className="text-gray-300" title={text}>
                              {text}
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          );
                        })()}
                        {column === 'comment' && (
                          alert.comment ? (
                            <span className="text-gray-300 truncate max-w-[150px] inline-block" title={alert.comment}>
                              {alert.comment}
                            </span>
                          ) : (
                            <span className="text-gray-600">-</span>
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
              })}
              {alertPadBottom > 0 && (
                <tr aria-hidden>
                  <td
                    colSpan={visibleColumns.length}
                    style={{ height: alertPadBottom, padding: 0, border: 0 }}
                  />
                </tr>
              )}
            </tbody>
          </table>
        )}

        {filteredAlerts.length > 0 && (
          <div className="py-2 px-3 flex items-center justify-center gap-3 border-t border-dark-border/50">
            <span className="font-numeric text-[10px] text-gray-500">
              {filteredAlerts.length !== allAlerts.length
                ? `Showing ${filteredAlerts.length} of ${allAlerts.length} loaded`
                : `${allAlerts.length} alerts`}
              {scopeLabel ? ` · ${fmtScopeLabel(scopeLabel)}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Filter Preset Modal */}
      <WiseguyFilterPresetModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSavePreset}
        preset={editingPreset}
      />

      {/* Settings Modal */}
      <WiseguySettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
        onSave={handleSaveSettings}
        settings={settings}
        initialTab={settingsInitialTab}
      />

      {/* Save Config Modal */}
      {showSaveConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-80 shadow-xl">
            <h3 className="text-sm font-semibold text-white mb-3">Save Widget Configuration</h3>
            <p className="text-xs text-gray-400 mb-3">
              Save your current column settings, highlights, filter presets, and audio settings.
            </p>
            <input
              type="text"
              value={newConfigName}
              onChange={(e) => setNewConfigName(e.target.value)}
              placeholder="Configuration name..."
              className="w-full px-3 py-2 text-sm bg-dark-bg border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow mb-3"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newConfigName.trim()) {
                  handleSaveWidgetConfig(newConfigName.trim());
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowSaveConfigModal(false);
                  setNewConfigName('');
                }}
                className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSaveWidgetConfig(newConfigName.trim())}
                disabled={!newConfigName.trim()}
                className="px-3 py-1.5 text-xs bg-bull-blue text-white rounded hover:bg-bull-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <WiseguyDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setDetailAlert(null);
        }}
        alert={detailAlert}
      />
    </div>
  );
}
